// deployment_class: customer_env_allowed
/**
 * The template being edited — app-level, not dashboard-local.
 *
 * ── Why a store rather than component state (E3 2026-08-29) ────────────────
 * E3 asked for New Template / Save Template / Save Template As in the PORTAL
 * dropdown, which lives in the shell's top bar. The template it acts on is
 * rendered by the dashboard. Two different component trees needing the same
 * state is exactly what a store is for; passing it down through the shell would
 * mean the shell knowing about dashboard internals.
 *
 * Matches how `auth-store` already works, so there is one state pattern in this
 * app rather than two.
 *
 * ── Persistence ────────────────────────────────────────────────────────────
 * The browser, for now. No server-side template store exists yet — and the
 * preferences store is not one: it is typed and drops unknown keys by design,
 * so a template put there would be silently discarded. When a real store lands,
 * only `load`/`persist` below change; nothing that calls this store moves.
 *
 * Every storage call is guarded. A browser with storage disabled must still get
 * a working dashboard — it simply does not remember edits between sessions,
 * which is a degradation rather than a failure.
 *
 * ── UPDATE 2026-09-20 · the real store landed (plan W0.8) ──────────────────
 * The paragraph above is preserved rather than rewritten, per
 * [[feedback_never_delete_archive_or_mark_old]]. What it predicted was ALMOST
 * right, and the part it got wrong is worth recording.
 *
 * A template now lives in Postgres across four tables and is served by
 * `/v3/templates` (migrations 0173/0174, `src/v3/templates/`). `localStorage`
 * is no longer the store — it is a per-browser CACHE, so a returning user sees
 * their arrangement on the first frame instead of the shipped default, and so a
 * browser that cannot reach the server still renders something.
 *
 * WHAT THE OLD NOTE GOT WRONG: "nothing that calls this store moves" could not
 * quite hold. `load()` was called SYNCHRONOUSLY at store construction
 * (`template: load()`), and a server read is asynchronous. Every method
 * signature is unchanged and no caller's shape moved — but ONE call site gains
 * a line: something has to TRIGGER the fetch after authentication, and a
 * synchronous constructor cannot. `hydrate()` is that trigger.
 *
 * The rest of the promise is kept by never blocking on the network: the store
 * initialises from cache-or-default immediately, so the dashboard renders on
 * the first frame, and the server's answer replaces it when it arrives.
 */
import { create } from 'zustand';
import { MODULES, MODULE_ORDER, type ModuleKey } from './dashboard-data';
import {
  templateFrom,
  renameTemplate,
  DEFAULT_ELEMENTS,
  type DashboardTemplate,
  type ModuleTemplate,
  type ElementKind,
  type ElementInstance,
} from './template-state';
import {
  copyTemplate,
  createTemplate,
  fetchDefaultTemplate,
  fetchTemplate,
  isStockTemplate,
  toEditable,
  updateTemplate,
  type ServerTemplate,
} from './template-api';

const STORAGE_KEY = 'kd-template';

/** The shipped shape — what a brand-new template starts from. */
export function freshTemplate(name?: string): DashboardTemplate {
  return templateFrom(MODULE_ORDER, (k: ModuleKey) => MODULES[k].tabs, name);
}

/**
 * Bring a stored template up to the current shape.
 *
 * ── Why this exists (2026-08-29, after a real break) ───────────────────────
 * `ModuleTemplate` moved from `hiddenElements: ElementKind[]` to an ordered
 * `elements: ElementInstance[]`, because drag-to-reorder needs somewhere to
 * record order. Templates already saved in a browser were written in the old
 * shape, so `m.elements` came back `undefined` and the first
 * `elements.some(...)` threw — white-screening the dashboard for anyone who had
 * ever saved a template. The tests all passed because they build a fresh
 * template every time and never read a stored one.
 *
 * **A persisted shape is a published contract.** Changing it obliges a
 * migration in the same commit; the version that shipped without one is the
 * defect, not the browser that stored the old shape faithfully.
 *
 * Everything here is TOLERANT by construction: a field that is missing or the
 * wrong type is repaired rather than trusted, because the input is data written
 * by an older version of this code and there is no way to renegotiate with it.
 */
function migrate(raw: unknown): DashboardTemplate | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const t = raw as Partial<DashboardTemplate> & Record<string, unknown>;
  if (!Array.isArray(t.modules) || t.modules.length === 0) return null;

  const modules: ModuleTemplate[] = t.modules.map((rawMod) => {
    const m = (rawMod ?? {}) as Partial<ModuleTemplate> & { hiddenElements?: ElementKind[] };

    // Old shape → new. Absence is now removal, so the placed set is the default
    // arrangement minus whatever was recorded as hidden, in the default order.
    let elements: ElementInstance[];
    if (Array.isArray(m.elements)) {
      elements = m.elements.filter(
        (e): e is ElementInstance =>
          typeof e === 'object' && e !== null && typeof (e as ElementInstance).kind === 'string',
      );
    } else {
      const hidden = Array.isArray(m.hiddenElements) ? m.hiddenElements : [];
      elements = DEFAULT_ELEMENTS.filter((k) => !hidden.includes(k)).map((kind) => ({
        id: kind,
        kind,
      }));
    }

    return {
      key: m.key as ModuleTemplate['key'],
      hidden: m.hidden === true,
      elements,
      tabs: Array.isArray(m.tabs) ? m.tabs : [],
      hiddenTabs: Array.isArray(m.hiddenTabs) ? m.hiddenTabs : [],
    };
  });

  // A module with no key cannot be rendered or addressed; drop it rather than
  // let it become an unnamed row in the rail.
  const usable = modules.filter((m) => typeof m.key === 'string' && m.key.length > 0);
  if (usable.length === 0) return null;

  return { name: typeof t.name === 'string' && t.name ? t.name : 'Kathedral default', modules: usable };
}

function load(): DashboardTemplate {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return freshTemplate();
    // A stored template that cannot be repaired is corrupt rather than empty —
    // falling back beats rendering a dashboard with no rail, and beats throwing.
    return migrate(JSON.parse(saved)) ?? freshTemplate();
  } catch {
    return freshTemplate();
  }
}

function persist(t: DashboardTemplate): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(t));
  } catch {
    /* storage unavailable — the template stays in memory for this session */
  }
}

export interface TemplateStore {
  template: DashboardTemplate;
  /** Developer is actively editing. Never true for a user — the UI gates it. */
  editing: boolean;
  /** Changed since the last save. Shown, never used to block. */
  dirty: boolean;

  /**
   * The server row this template came from, once one is known.
   *
   * `null` before hydration, or when the server could not be reached — in which
   * case saving CREATES rather than failing, so work is never lost to a
   * connection that dropped between opening and saving.
   */
  serverId: string | null;
  /** True when the loaded template is the product's, not this customer's. */
  isStock: boolean;
  /** The server has answered. Until then the template is cache-or-default. */
  hydrated: boolean;
  /** Set when the last write failed, so the UI can say so instead of lying. */
  saveError: string | null;

  /** Apply an edit. Every mutation goes through here so `dirty` is never guessed. */
  edit: (f: (t: DashboardTemplate) => DashboardTemplate) => void;
  setEditing: (v: boolean) => void;
  /** Commit the template as it stands. This is what turning editing off does. */
  save: () => void;
  /** Commit under a new name. */
  saveAs: (name: string) => void;
  /** Start again from the shipped shape, under a new name. */
  newTemplate: (name: string) => void;

  /** Read this user's template from the server. Safe to call more than once. */
  hydrate: () => void;
  /** Open a specific template by server id. */
  open: (id: string) => void;
}

/**
 * Adopt a template the server returned.
 *
 * Also writes the cache, so the NEXT first frame shows this arrangement rather
 * than the shipped default. That is the whole remaining job of `localStorage`
 * here — it is a head start, never the truth.
 */
function adopt(t: ServerTemplate): Partial<TemplateStore> {
  // Through `migrate` rather than trusting the payload.
  //
  // A response is AT LEAST as untrusted as stored data: it crosses a version
  // boundary the same way, and it can also be a truncated body or an older
  // server. `toEditable` alone let a payload with no `modules` through, and
  // `visibleModules` then threw on `undefined.filter` — white-screening the
  // dashboard, which is exactly the failure `migrate` was written for after it
  // happened once already (see the 2026-08-29 note above). Caught by the
  // existing dashboard suite, 2026-09-20.
  const editable = migrate(toEditable(t)) ?? freshTemplate(t.name);
  persist(editable);
  return {
    template: editable,
    serverId: t.id,
    isStock: isStockTemplate(t),
    hydrated: true,
    dirty: false,
    saveError: null,
  };
}

/**
 * The message shown when a write fails.
 *
 * `ApiError` carries a problem-detail title; anything else is reported as
 * itself. Never swallowed: a save that silently did nothing is how someone
 * loses an afternoon's arrangement and only finds out tomorrow.
 */
function messageOf(err: unknown): string {
  return err instanceof Error && err.message ? err.message : 'Could not save the template';
}

export const useTemplateStore = create<TemplateStore>((set, get) => ({
  // Cache-or-default, synchronously — the dashboard renders on the first frame
  // and never waits on the network to draw itself.
  template: load(),
  editing: false,
  dirty: false,
  serverId: null,
  isStock: false,
  hydrated: false,
  saveError: null,

  edit: (f) => set((s) => ({ template: f(s.template), dirty: true })),
  setEditing: (v) => set({ editing: v }),

  hydrate: () => {
    // Re-hydrating over unsaved edits would discard them without asking.
    if (get().hydrated || get().dirty) return;
    void fetchDefaultTemplate()
      .then((t) => set(adopt(t)))
      // A dashboard that renders the shipped shape beats one that renders
      // nothing. The failure is recorded, not thrown at the user mid-render.
      .catch((err: unknown) => set({ hydrated: true, saveError: messageOf(err) }));
  },

  open: (id) => {
    void fetchTemplate(id)
      .then((t) => set(adopt(t)))
      .catch((err: unknown) => set({ saveError: messageOf(err) }));
  },

  /**
   * Commit the template as it stands.
   *
   * Three cases, and the branch is the copy-on-edit rule made visible:
   *   stock      → COPY. The product's default is not this customer's to edit,
   *                and the server refuses it regardless (the row is not in the
   *                set the statement addresses), so asking would only produce a
   *                404 where the user expects a save.
   *   known id   → UPDATE in place.
   *   no id      → CREATE. Reached when hydration failed; saving still works.
   */
  save: () => {
    const { template, serverId, isStock } = get();
    const write =
      serverId === null
        ? createTemplate(template)
        : isStock
          ? copyTemplate(serverId, template.name)
          : updateTemplate(serverId, template);

    void write
      .then((t) => set(adopt(t)))
      .catch((err: unknown) => set({ saveError: messageOf(err) }));
  },

  saveAs: (name) => {
    const renamed = renameTemplate(get().template, name);
    // Optimistic locally so the new name appears immediately; the server's
    // answer replaces it, including the id that makes the next save an update.
    set({ template: renamed });
    void createTemplate(renamed)
      .then((t) => set(adopt(t)))
      .catch((err: unknown) => set({ saveError: messageOf(err) }));
  },

  /**
   * A new template starts from the shipped shape rather than from whatever was
   * on screen — otherwise "new" would inherit the last template's removals and
   * quietly not be new at all.
   */
  newTemplate: (name) => {
    const fresh = freshTemplate(name.trim() === '' ? undefined : name);
    set({ template: fresh, dirty: false, editing: true, serverId: null, isStock: false });
    void createTemplate(fresh)
      .then((t) => set({ ...adopt(t), editing: true }))
      .catch((err: unknown) => set({ saveError: messageOf(err) }));
  },
}));
